// eslint-disable-next-line import/no-extraneous-dependencies, simple-import-sort/imports
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { ModuleRegistry, } from '@ag-grid-community/core';
import { AgGridReact } from '@ag-grid-community/react';
import { ChangeDetectionStrategyType } from '@ag-grid-community/react/lib/shared/changeDetectionService';
import cx from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LoadingIndicator } from '../../loading-indicator';
import { getOnEnter } from '../../_utils';
import { disableClickGridCells } from '../table-utils';
import { CELL_TYPE_COMPONENTS } from './cell/cell-type-service';
import { addCommonCells, DEFAULT_COL_DEF, } from './header/header-definition-service/header-definition-service';
import cssVars from './scss-to-js-vars.module.scss';
import styles from './table-list-view.module.scss';
import './table-list-view-override.scss';
ModuleRegistry.registerModules([ClientSideRowModelModule]);
export const TableListView = ({ data, columns, options: { hasCheckboxes = false, hideSelectAllCheckbox = false, onRowsSelected, selectedRowsIds = [], isRowSelectable, suppressMovable = false, 
// suppressRowClickSelection = true,
checkboxHeaderClass = '', customWidth, }, rowMenu, hideRowCheckbox, rowActionItems, onFavouriteClickHandler, onAvatarClick, onSortingChanged, onRowClickHandler, onRowCommandClickHandler, loading, agGridContext, onRowDoubleClickHandler, disableClickCells = [], immutableData = true, headerHeight, rowHeight, rowSelection = 'multiple', isRowClickable, refreshAfterOnClick = false, setAgGridApi, hideLoader = false, onCellKeyDown: onCustomCellKeyDown, updateAriaAttributes = false, ...restProps }) => {
    const [gridApi, setGridApi] = useState(null);
    const allDisableClickGridCells = [
        ...disableClickGridCells,
        ...disableClickCells,
    ];
    const gridRef = useRef(null);
    const onRowClickedWrapper = (event) => {
        var _a, _b;
        if (!isRowClickable || isRowClickable(event.data)) {
            const cellId = (_b = (_a = gridApi === null || gridApi === void 0 ? void 0 : gridApi.getFocusedCell()) === null || _a === void 0 ? void 0 : _a.column) === null || _b === void 0 ? void 0 : _b.getColId();
            if (!cellId || !allDisableClickGridCells.includes(cellId)) {
                const mouseEvent = event.event;
                if (((mouseEvent === null || mouseEvent === void 0 ? void 0 : mouseEvent.ctrlKey) || (mouseEvent === null || mouseEvent === void 0 ? void 0 : mouseEvent.metaKey)) &&
                    onRowCommandClickHandler) {
                    onRowCommandClickHandler(event.data);
                }
                else if (onRowClickHandler) {
                    onRowClickHandler(event.data);
                }
            }
        }
    };
    const defaultOnCellKeyDown = useCallback((e) => {
        var _a, _b;
        if (!e.event) {
            return;
        }
        const keyboardEvent = e.event;
        const key = keyboardEvent.key;
        if (key === 'Enter') {
            if (!isRowClickable || isRowClickable(e.data)) {
                const cellId = (_b = (_a = gridApi === null || gridApi === void 0 ? void 0 : gridApi.getFocusedCell()) === null || _a === void 0 ? void 0 : _a.column) === null || _b === void 0 ? void 0 : _b.getColId();
                if (!cellId || !allDisableClickGridCells.includes(cellId)) {
                    // const mouseEvent = event.event as MouseEvent;
                    if (onRowCommandClickHandler) {
                        onRowCommandClickHandler(e.data);
                    }
                    else if (onRowClickHandler) {
                        onRowClickHandler(e.data);
                    }
                }
            }
        }
    }, [
        allDisableClickGridCells,
        gridApi,
        isRowClickable,
        onRowClickHandler,
        onRowCommandClickHandler,
    ]);
    const onCellKeyDown = useCallback((event) => {
        // Use the custom handler if provided
        if (onCustomCellKeyDown) {
            onCustomCellKeyDown(event);
        }
        else {
            defaultOnCellKeyDown(event);
        }
    }, [onCustomCellKeyDown, defaultOnCellKeyDown]);
    const onRowDoubleClickedWrapper = (event) => {
        var _a, _b;
        if (onRowDoubleClickHandler) {
            const cellId = (_b = (_a = gridApi === null || gridApi === void 0 ? void 0 : gridApi.getFocusedCell()) === null || _a === void 0 ? void 0 : _a.column) === null || _b === void 0 ? void 0 : _b.getColId();
            if (!cellId || !disableClickCells.includes(cellId)) {
                onRowDoubleClickHandler(event.data);
            }
        }
    };
    const getRowNodeId = (rowData) => rowData.id;
    const onGridReady = async (event) => {
        await setGridApi(event.api);
        if (setAgGridApi) {
            setAgGridApi(event.api);
        }
        if (loading && !hideLoader) {
            event.api.showLoadingOverlay();
        }
    };
    const preSelectRows = (api) => {
        if (!selectedRowsIds.length) {
            return false;
        }
        let areRowsSelected = false;
        data.forEach(({ id }) => {
            const currentNode = api.getRowNode(id);
            if (selectedRowsIds.indexOf(id) !== -1) {
                currentNode.setSelected(true);
                areRowsSelected = true;
            }
            else {
                currentNode.setSelected(false);
            }
        });
        return areRowsSelected;
    };
    useEffect(() => {
        if (!gridApi) {
            return;
        }
        if (loading) {
            gridApi.showLoadingOverlay();
        }
        else {
            gridApi.hideOverlay();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, data]);
    useEffect(() => {
        var _a;
        if (!gridApi) {
            return;
        }
        if (!selectedRowsIds.length) {
            gridApi.deselectAll();
            return;
        }
        const areRowsSelected = preSelectRows(gridApi);
        if (!areRowsSelected) {
            gridApi.deselectAll();
        }
        // Update ARIA attributes for the middle viewport
        if (updateAriaAttributes && gridRef.current) {
            const middleViewport = gridRef.current.querySelector('.ag-body-viewport');
            if (middleViewport) {
                const ariaLabel = selectedRowsIds.length > 1
                    ? `Selected Rows: ${selectedRowsIds.length}`
                    : `Selected Row: ${((_a = gridApi.getRowNode(selectedRowsIds[0])) === null || _a === void 0 ? void 0 : _a.data.name) || 'Row'}`;
                middleViewport.setAttribute('role', 'group');
                // dynamic aria-label that will be updated based on the selected row
                middleViewport.setAttribute('aria-label', ariaLabel);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRowsIds, gridApi]);
    // Accessibility fix: Handle Enter key on header cells to trigger sorting
    const handleHeaderEnter = (event) => {
        var _a;
        const target = event.target;
        if ((_a = target === null || target === void 0 ? void 0 : target.classList) === null || _a === void 0 ? void 0 : _a.contains('ag-header-cell')) {
            event.preventDefault();
            const headerButton = target.querySelector('[role="button"]');
            if (headerButton && typeof headerButton.click === 'function') {
                headerButton.click();
            }
        }
    };
    const handleHeaderKeyDown = getOnEnter(handleHeaderEnter);
    const loadingOverlayParams = {
        hideLoader,
    };
    const noRowsOverlayComponent = hideLoader ? '' : 'customLoadingOverlay';
    const frameworkComponent = {
        customLoadingOverlay: () => (React.createElement(LoadingIndicator.Global, { className: styles.loadingContainer },
            React.createElement("div", null))),
        ...CELL_TYPE_COMPONENTS,
    };
    const onSortChanged = (e) => {
        const sortStateModel = e.columnApi
            .getColumnState()
            .find(({ sort }) => !!sort);
        if (onSortingChanged && sortStateModel)
            onSortingChanged([sortStateModel]);
    };
    const onSelectionChanged = (e) => {
        const selectedIds = e.api
            .getSelectedNodes()
            .map((node) => node.id)
            .filter((element) => element !== undefined);
        if (onRowsSelected)
            onRowsSelected(selectedIds);
    };
    const onRowDataUpdated = (e) => {
        const areRowsSelected = preSelectRows(e.api);
        if (!areRowsSelected) {
            if (onRowsSelected)
                onRowsSelected([]);
        }
    };
    const addCommonCellProps = {
        columns,
        hasCheckboxes,
        rowMenu,
        rowActionItems,
        hideSelectAllCheckbox,
        suppressMovable,
        headerClass: checkboxHeaderClass,
        customWidth,
    };
    const [tableColumns, setTableColumns] = useState(addCommonCells(addCommonCellProps));
    useEffect(() => {
        setTableColumns(addCommonCells(addCommonCellProps));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasCheckboxes]); // can add more items from addCommonCellProps if required
    const getRowHeight = () => rowHeight !== null && rowHeight !== void 0 ? rowHeight : Number(cssVars.metadatalistrowheight);
    const context = {
        rowMenu,
        hideRowCheckbox,
        rowActionItems,
        onFavouriteClickHandler,
        onAvatarClick,
        ...agGridContext,
    };
    const getRowClass = isRowClickable
        ? (params) => {
            return isRowClickable(params.data)
                ? undefined
                : styles.rowNotClickable;
        }
        : undefined;
    return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    React.createElement("div", { className: cx(styles.listViewContainer, {
            [styles.clickableRows]: onRowClickHandler,
        }), ref: gridRef, onKeyDown: handleHeaderKeyDown },
        React.createElement(AgGridReact, { tabIndex: 0, defaultColDef: DEFAULT_COL_DEF, columnDefs: tableColumns, context: context, components: frameworkComponent, rowData: data, modules: [ClientSideRowModelModule], suppressRowClickSelection: true, rowSelection: rowSelection, suppressNoRowsOverlay: hideLoader, rowClass: "list-view-row-container", headerHeight: headerHeight !== null && headerHeight !== void 0 ? headerHeight : Number(cssVars.metadatalistrowheight), getRowHeight: getRowHeight, onRowClicked: (event) => {
                onRowClickedWrapper(event);
                if (refreshAfterOnClick) {
                    gridApi === null || gridApi === void 0 ? void 0 : gridApi.refreshCells();
                }
            }, onCellKeyDown: onCellKeyDown, getRowNodeId: getRowNodeId, onGridReady: onGridReady, loadingOverlayComponent: noRowsOverlayComponent, loadingOverlayComponentParams: loadingOverlayParams, 
            // using similar overlay for no rows, assuming components
            // will handle an empty ([]) state outside this component
            noRowsOverlayComponent: noRowsOverlayComponent, noRowsOverlayComponentParams: loadingOverlayParams, onSortChanged: onSortChanged, onRowDataUpdated: onRowDataUpdated, onSelectionChanged: onSelectionChanged, onRowDoubleClicked: onRowDoubleClickedWrapper, domLayout: "autoHeight", onRowDataChanged: onRowDataUpdated, rowDataChangeDetectionStrategy: ChangeDetectionStrategyType.DeepValueCheck, suppressLoadingOverlay: hideLoader, immutableData: immutableData, isRowSelectable: isRowSelectable, getRowClass: getRowClass, ...restProps, suppressDragLeaveHidesColumns: true })));
};
//# sourceMappingURL=table-list-view.js.map